import { ApolloError } from '@apollo/client';
import { Box, Grid, Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { Footer, FOOTER_HEIGHT } from '@/components/navigation/Footer';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { ClientProfessionalTeamForm } from '@/modules/professionalTeam/ClientProfessionalTeam.types';

import {
  ClientProfessionalTeamContextProvider,
  ClientProfessionalTeamContextType,
} from './ClientProfessionalTeam.context';
import {
  mapDataToColumn,
  mapDataToPrincipalClientDisplay,
  mapDataToTeamMemberOptions,
  mapFormDataToAugmentedUpdates,
} from './ClientProfessionalTeam.utils';
import { ClientProfessionalTeamAdvisorClient } from './ClientProfessionalTeamAdvisorClient';
import { ClientProfessionalTeamPrincipalClients } from './ClientProfessionalTeamPrincipalClients';
import {
  ClientProfessionalTeamDocument,
  ClientProfessionalTeamQuery,
  useClientProfessionalTeamQuery,
} from './graphql/ClientProfessionalTeam.generated';
import { useUpdateClientProfessionalTeamMutation } from './graphql/UpdateClientProfessionalTeam.generated';

export interface ClientProfessionalTeamProps {
  householdId: string;
}

function ClientProfessionalTeamInner({
  householdId,
}: ClientProfessionalTeamProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const canEdit = useCanEditHouseholdData();
  const formMethods = useForm<ClientProfessionalTeamForm>({
    defaultValues: {
      advisorClientRoles: {},
      principalClientIdTeamMemberMap: {},
    },
  });
  const { reset, handleSubmit, formState } = formMethods;
  const resetForm = useCallback(
    (queryData: ClientProfessionalTeamQuery) => {
      reset(mapDataToColumn(queryData));
    },
    [reset]
  );
  const { data, loading } = useClientProfessionalTeamQuery({
    variables: { householdId },
    onError: (error: ApolloError) => {
      showFeedback(
        "Could not load the client's professional team.  Please refresh the page and try again."
      );
      reportError('Caught error when loading professional team', error);
    },
    onCompleted: (queryData) => {
      resetForm(queryData);
    },
  });

  const [updateClientProfessionalTeam, { loading: isSaving }] =
    useUpdateClientProfessionalTeamMutation({
      refetchQueries: [ClientProfessionalTeamDocument],
      awaitRefetchQueries: true,
      onError: (error: ApolloError) => {
        showFeedback(
          'Could not save client professional team changes. Please try again.'
        );
        reportError(
          'Encountered an error when saving client professional team',
          error
        );
      },
      onCompleted: () => {
        showFeedback('Client professional team updated', {
          variant: 'success',
        });
        reset({}, { keepValues: true });
      },
    });

  const onValidSubmit: SubmitHandler<ClientProfessionalTeamForm> = (
    formData: ClientProfessionalTeamForm
  ) => {
    const { clientProfileUpdates, clientOrganizationUpdates } =
      mapFormDataToAugmentedUpdates(formData, data, householdId);

    return updateClientProfessionalTeam({
      variables: {
        householdId,
        clientProfileInputs: clientProfileUpdates,
        clientOrganizationInputs: clientOrganizationUpdates,
      },
    });
  };

  const onSubmit = handleSubmit(onValidSubmit);

  const teamMemberOptions = useMemo<
    ClientProfessionalTeamContextType['teamMemberOptions']
  >(() => mapDataToTeamMemberOptions(data), [data]);

  const principalClientDisplayInfo = useMemo<Record<string, string>>(() => {
    return mapDataToPrincipalClientDisplay(data);
  }, [data]);

  return (
    <Box position="relative">
      <HouseholdPermissionAwareDisabledProvider
        forceDisabled={loading || isSaving}
      >
        <ClientProfessionalTeamContextProvider
          teamMemberOptions={teamMemberOptions}
          principalClientDisplayInfo={principalClientDisplayInfo}
          householdId={householdId}
        >
          <FormProvider {...formMethods}>
            <form onSubmit={onSubmit} noValidate>
              <Grid
                container
                columns={12}
                columnSpacing={2}
                mb={`${FOOTER_HEIGHT}px`}
                p={2}
              >
                <Grid item md={12} lg={6}>
                  <ClientProfessionalTeamAdvisorClient />
                </Grid>
                <Grid item md={12} lg={6}>
                  <ClientProfessionalTeamPrincipalClients />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </ClientProfessionalTeamContextProvider>

        {canEdit && (
          <Box position="sticky" sx={{ bottom: 0, left: 0, right: 0 }}>
            <Footer
              rightAction={
                <Stack direction="row" flexWrap="nowrap" spacing={1}>
                  {formState.isDirty && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        if (data) {
                          resetForm(data);
                        }
                        showFeedback('Client team changes cleared', {
                          variant: 'info-high',
                        });
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onSubmit}
                    disabled={!formState.isDirty}
                    loading={formMethods.formState.isSubmitting}
                  >
                    Save changes
                  </Button>
                </Stack>
              }
            />
          </Box>
        )}
      </HouseholdPermissionAwareDisabledProvider>
    </Box>
  );
}

export function ClientProfessionalTeam() {
  const householdId = useRequiredParam('householdId');
  return <ClientProfessionalTeamInner householdId={householdId} />;
}
